import React, { useEffect, useRef, useState } from "react";
import { X, Send, Heart, Radio, Users, Mic, MicOff, SwitchCamera, Gauge, ShoppingBag, Pin, Loader2, Sparkles, Volume2, VolumeX, StopCircle } from "lucide-react";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl } from "@/lib/uploads";
import { videoPoster } from "@/lib/imageCompress";
import { encodeImageMeta } from "@/lib/blur";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const LIVE_EMOJIS = ["❤️", "😂", "👏", "🔥", "😮", "🎉"];
let floatId = 0;

// TikTok-style camera filters (CSS applied via a canvas so viewers see them too).
const FILTERS = [
  { key: "none", css: "none" },
  { key: "soft", css: "brightness(1.12) contrast(0.95) saturate(1.12) blur(0.6px)" },
  { key: "warm", css: "saturate(1.35) sepia(0.22) contrast(1.05) brightness(1.05)" },
  { key: "cool", css: "saturate(1.1) hue-rotate(-12deg) brightness(1.06) contrast(1.05)" },
  { key: "vivid", css: "saturate(1.7) contrast(1.16) brightness(1.03)" },
  { key: "mono", css: "grayscale(1) contrast(1.1)" },
];
const FILTER_CSS = Object.fromEntries(FILTERS.map((f) => [f.key, f.css]));

// Lightweight WebRTC live: the host publishes one stream to each viewer (best for
// a small audience on weak 4G). Signaling + chat + reactions ride the app WebSocket.
export default function LiveScreen({ role, session, sendWs, onClose }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isHost = role === "host";
  const [live, setLive] = useState(isHost ? null : session); // resolved session (host creates it)
  const [viewers, setViewers] = useState(0);
  const [comments, setComments] = useState([]);
  const [floats, setFloats] = useState([]);
  const [text, setText] = useState("");
  const [status, setStatus] = useState(isHost ? t("live_starting") : t("live_connecting"));
  const [micOn, setMicOn] = useState(true);
  const [dataSaver, setDataSaver] = useState(true); // default ON for weak nets
  const [ended, setEnded] = useState(false);
  const [pinned, setPinned] = useState(session?.pinned_product || null);
  const [showPin, setShowPin] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState("none");
  const [showFilters, setShowFilters] = useState(false);
  const [viewerMuted, setViewerMuted] = useState(false);

  const outStreamRef = useRef(null);   // host: the stream actually sent to viewers
  const camVideoRef = useRef(null);    // hidden <video> source for the filter canvas
  const canvasRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const filterTrackRef = useRef(null);
  const rafRef = useRef(null);
  const filterRef = useRef("none");

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const liveIdRef = useRef(session?.id || null);
  const streamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peersRef = useRef({});        // host: viewerId -> pc ; viewer: hostId -> pc
  const iceRef = useRef([]);
  const pendingIce = useRef({});      // peerId -> [candidates]
  const facingRef = useRef("environment");

  const addFloat = (emoji) => {
    const id = ++floatId;
    setFloats((f) => [...f.slice(-24), { id, emoji, left: 10 + Math.random() * 70 }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 2600);
  };

  const newPc = () => {
    const pc = new RTCPeerConnection({ iceServers: iceRef.current });
    return pc;
  };

  const capViewerBitrate = async (pc) => {
    for (const s of pc.getSenders()) {
      if (!s.track) continue;
      const p = s.getParameters();
      if (!p.encodings || !p.encodings.length) p.encodings = [{}];
      if (s.track.kind === "video") {
        p.encodings[0].maxBitrate = dataSaver ? 350000 : 900000;
        p.encodings[0].scaleResolutionDownBy = dataSaver ? 2 : 1;
        p.encodings[0].maxFramerate = dataSaver ? 20 : 30;
      } else {
        p.encodings[0].maxBitrate = 48000;
      }
      try { await s.setParameters(p); } catch (e) {}
    }
  };

  // ---- HOST: create an offer for each joining viewer -----------------------
  const hostOfferTo = async (viewerId) => {
    const pc = newPc();
    peersRef.current[viewerId] = pc;
    const out = outStreamRef.current || streamRef.current;
    out.getTracks().forEach((tr) => pc.addTrack(tr, out));
    pc.onicecandidate = (e) => { if (e.candidate) sendWs({ type: "live_signal", live_id: liveIdRef.current, to: viewerId, data: { candidate: e.candidate } }); };
    pc.onconnectionstatechange = () => { if (["failed", "disconnected", "closed"].includes(pc.connectionState)) { try { pc.close(); } catch (e) {} delete peersRef.current[viewerId]; } };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await capViewerBitrate(pc);
    sendWs({ type: "live_signal", live_id: liveIdRef.current, to: viewerId, data: { sdp: offer } });
  };

  // ---- VIEWER: answer the host's offer ------------------------------------
  const viewerAnswer = async (hostId, sdp) => {
    const pc = newPc();
    peersRef.current[hostId] = pc;
    pc.ontrack = (e) => { if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0]; setStatus(""); };
    pc.onicecandidate = (e) => { if (e.candidate) sendWs({ type: "live_signal", live_id: liveIdRef.current, to: hostId, data: { candidate: e.candidate } }); };
    pc.onconnectionstatechange = () => { if (pc.connectionState === "connected") setStatus(""); };
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    (pendingIce.current[hostId] || []).forEach((c) => pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {}));
    pendingIce.current[hostId] = [];
    const ans = await pc.createAnswer();
    await pc.setLocalDescription(ans);
    sendWs({ type: "live_signal", live_id: liveIdRef.current, to: hostId, data: { sdp: ans } });
  };

  const addIce = async (peerId, candidate) => {
    const pc = peersRef.current[peerId];
    if (pc && pc.remoteDescription && pc.remoteDescription.type) {
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) {}
    } else {
      pendingIce.current[peerId] = [...(pendingIce.current[peerId] || []), candidate];
    }
  };

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    Object.values(peersRef.current).forEach((pc) => { try { pc.close(); } catch (e) {} });
    peersRef.current = {};
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    canvasStreamRef.current?.getTracks().forEach((tr) => tr.stop());
  };

  // ---- lifecycle ----------------------------------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: iceData } = await api.get("/rtc/ice");
        iceRef.current = iceData.iceServers;
        if (isHost) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } },
          });
          if (!alive) { stream.getTracks().forEach((tr) => tr.stop()); return; }
          streamRef.current = stream;
          outStreamRef.current = new MediaStream(stream.getTracks());
          if (localVideoRef.current) localVideoRef.current.srcObject = stream;
          const { data } = await api.post("/live/start", { module: session.module, product_id: session.product_id, title: session.title, max_viewers: session.max_viewers || 0 });
          liveIdRef.current = data.id;
          setLive(data);
          setStatus("");
          // Record for an optional replay (best-effort; ignored if unsupported)
          try {
            const mr = new MediaRecorder(stream, { mimeType: "video/webm;codecs=vp8,opus" });
            mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
            mr.start(1000);
            recorderRef.current = mr;
          } catch (e) {}
        } else {
          liveIdRef.current = session.id;
          sendWs({ type: "live_join", live_id: session.id });
          api.get(`/live/${session.id}`).then(({ data }) => { if (data.pinned_product) setPinned(data.pinned_product); }).catch(() => {});
        }
      } catch (e) {
        toast.error(isHost ? t("live_cam_fail") : t("live_join_fail"));
        onClose();
      }
    })();

    const onLive = async (ev) => {
      const m = ev.detail;
      if (m.live_id && liveIdRef.current && m.live_id !== liveIdRef.current) return;
      if (m.type === "live_join" && isHost) { hostOfferTo(m.from); }
      else if (m.type === "live_leave" && isHost) { const pc = peersRef.current[m.from]; if (pc) { try { pc.close(); } catch (e) {} delete peersRef.current[m.from]; } }
      else if (m.type === "live_viewers") { setViewers(m.count); }
      else if (m.type === "live_signal") {
        const d = m.data || {};
        if (isHost) { const pc = peersRef.current[m.from]; if (pc && d.sdp && d.sdp.type === "answer") { try { await pc.setRemoteDescription(new RTCSessionDescription(d.sdp)); } catch (e) {} } if (d.candidate) addIce(m.from, d.candidate); }
        else { if (d.sdp && d.sdp.type === "offer") await viewerAnswer(m.from, d.sdp); if (d.candidate) addIce(m.from, d.candidate); }
      }
      else if (m.type === "live_comment") { setComments((c) => [...c.slice(-40), { id: `${m.from}-${Date.now()}-${Math.random()}`, name: m.from_name, text: m.text }]); }
      else if (m.type === "live_reaction") { addFloat(m.emoji); }
      else if (m.type === "live_pin") { setPinned(m.product); }
      else if (m.type === "live_full") { if (!isHost) { toast.error(t("live_full")); onClose(); } }
      else if (m.type === "live_ended") { if (!isHost) { setEnded(true); setStatus(t("live_ended")); cleanup(); } }
    };
    window.addEventListener("rc-live", onLive);
    return () => { alive = false; window.removeEventListener("rc-live", onLive); cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply bitrate cap when data-saver toggles (host).
  useEffect(() => { if (isHost) Object.values(peersRef.current).forEach((pc) => capViewerBitrate(pc)); }, [dataSaver]); // eslint-disable-line

  const openPinPicker = async () => {
    setShowPin(true);
    try { const { data } = await api.get("/market/my-products"); setMyProducts(data); } catch {}
  };
  const pinProduct = async (pid) => {
    try { const { data } = await api.post(`/live/${liveIdRef.current}/pin`, { product_id: pid }); setPinned(data.product); setShowPin(false); } catch {}
  };
  const unpin = async () => { try { await api.post(`/live/${liveIdRef.current}/pin`, { product_id: null }); setPinned(null); } catch {} };
  const openPinned = () => { if (!pinned) return; window.dispatchEvent(new CustomEvent("rc-open-product", { detail: { pid: pinned.id } })); onClose(); };

  const stopRecorder = () => new Promise((resolve) => {
    const mr = recorderRef.current;
    if (!mr || mr.state === "inactive") return resolve(null);
    mr.onstop = () => resolve(new Blob(chunksRef.current, { type: "video/webm" }));
    try { mr.stop(); } catch { resolve(null); }
  });

  const postReplay = async (blob) => {
    if (!blob || blob.size < 10000) return;
    try {
      setPosting(true);
      const file = new File([blob], `live-replay-${Date.now()}.webm`, { type: "video/webm" });
      const { url } = await uploadFileChunked(file, null, "social");
      let poster, meta = {};
      try { const pf = await videoPoster(file); if (pf) { meta = await encodeImageMeta(pf).catch(() => ({})); poster = (await uploadFileChunked(pf, null, "social")).url; } } catch {}
      await api.post("/social/posts", { text: t("live_replay_caption"), visibility: "public", media: [{ url, type: "video", name: "Live replay", poster, blur_hash: meta.blur_hash, w: meta.media_w, h: meta.media_h }] });
      toast.success(t("live_replay_posted"));
    } catch { toast.error(t("s_could_not_post")); } finally { setPosting(false); }
  };

  const endLive = async () => {
    if (isHost && liveIdRef.current) {
      try { await api.post(`/live/${liveIdRef.current}/stop`); } catch (e) {}
      const blob = await stopRecorder();
      if (blob && blob.size > 10000 && window.confirm(t("live_post_replay_q"))) { await postReplay(blob); }
    } else if (liveIdRef.current) {
      sendWs({ type: "live_leave", live_id: liveIdRef.current });
    }
    cleanup();
    onClose();
  };

  const sendComment = () => {
    if (!text.trim() || !liveIdRef.current) return;
    sendWs({ type: "live_comment", live_id: liveIdRef.current, text: text.trim() });
    setComments((c) => [...c.slice(-40), { id: `me-${Date.now()}`, name: user.name, text: text.trim() }]);
    setText("");
  };
  const sendReaction = (emoji) => { if (liveIdRef.current) { sendWs({ type: "live_reaction", live_id: liveIdRef.current, emoji }); addFloat(emoji); } };

  const toggleMic = () => { const tr = streamRef.current?.getAudioTracks()[0]; if (tr) { tr.enabled = !tr.enabled; setMicOn(tr.enabled); } };
  const switchCamera = async () => {
    const next = facingRef.current === "user" ? "environment" : "user";
    try {
      const ns = await navigator.mediaDevices.getUserMedia({ video: { facingMode: next, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      const nt = ns.getVideoTracks()[0];
      const old = streamRef.current?.getVideoTracks()[0];
      if (old) { streamRef.current.removeTrack(old); old.stop(); }
      streamRef.current?.addTrack(nt);
      if (filterRef.current === "none") {
        Object.values(peersRef.current).forEach((pc) => { const s = pc.getSenders().find((x) => x.track && x.track.kind === "video"); if (s) s.replaceTrack(nt); });
        if (localVideoRef.current) localVideoRef.current.srcObject = streamRef.current;
      }
      facingRef.current = next;
    } catch (e) { toast.error(t("live_cam_switch_fail")); }
  };

  // ---- Camera filters (host) — rendered via canvas so viewers see them too ----
  const setOutgoingVideo = (track) => {
    Object.values(peersRef.current).forEach((pc) => { const s = pc.getSenders().find((x) => x.track && x.track.kind === "video"); if (s) s.replaceTrack(track).catch(() => {}); });
    const out = outStreamRef.current;
    if (out) { const old = out.getVideoTracks()[0]; if (old && old !== track) out.removeTrack(old); if (!out.getVideoTracks().includes(track)) out.addTrack(track); }
  };

  const startFilterCanvas = () => {
    if (canvasStreamRef.current) return;
    const cam = camVideoRef.current, canvas = canvasRef.current;
    cam.srcObject = streamRef.current;
    cam.muted = true; cam.play().catch(() => {});
    const draw = () => {
      const w = cam.videoWidth || 720, h = cam.videoHeight || 1280;
      if (canvas.width !== w) { canvas.width = w; canvas.height = h; }
      const ctx = canvas.getContext("2d");
      ctx.filter = FILTER_CSS[filterRef.current] || "none";
      try { ctx.drawImage(cam, 0, 0, w, h); } catch (e) {}
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    canvasStreamRef.current = canvas.captureStream(dataSaver ? 20 : 30);
    filterTrackRef.current = canvasStreamRef.current.getVideoTracks()[0];
  };

  const applyFilter = (key) => {
    setFilter(key);
    filterRef.current = key;
    if (!isHost) return;
    if (key === "none") {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      const raw = streamRef.current?.getVideoTracks()[0];
      if (raw) setOutgoingVideo(raw);
      if (localVideoRef.current) localVideoRef.current.srcObject = streamRef.current;
      return;
    }
    startFilterCanvas();
    if (filterTrackRef.current) {
      setOutgoingVideo(filterTrackRef.current);
      if (localVideoRef.current) localVideoRef.current.srcObject = new MediaStream([filterTrackRef.current]);
    }
  };

  // Viewer: mute/unmute the live audio locally
  useEffect(() => { if (!isHost && remoteVideoRef.current) remoteVideoRef.current.muted = viewerMuted; }, [viewerMuted, isHost, status]);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="live-screen">
      <video ref={isHost ? localVideoRef : remoteVideoRef} autoPlay playsInline muted={isHost || viewerMuted} className="absolute inset-0 h-full w-full object-cover" data-testid="live-video" />
      {/* hidden source video + canvas for host camera filters (rendered off-screen so frames keep decoding) */}
      <video ref={camVideoRef} playsInline muted className="absolute w-px h-px opacity-0 pointer-events-none -z-10" />
      <canvas ref={canvasRef} className="absolute w-px h-px opacity-0 pointer-events-none -z-10" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center gap-2 p-3 pt-4">
        <span className="flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white"><Radio size={13} /> {t("live_badge")}</span>
        <span className="flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-xs text-white" data-testid="live-viewers"><Users size={13} /> {viewers}</span>
        <div className="flex-1 min-w-0">
          <div className="truncate text-sm font-medium text-white">{(live || session)?.host_name || user.name}</div>
          {(live || session)?.title ? <div className="truncate text-[11px] text-white/70">{(live || session).title}</div> : null}
        </div>
        {isHost && <button onClick={endLive} data-testid="live-end" className="flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-bold text-white active:scale-95"><StopCircle size={15} /> {t("live_end_btn")}</button>}
        <button onClick={endLive} data-testid="live-close" className="h-9 w-9 grid place-items-center rounded-full bg-black/50 text-white"><X size={20} /></button>
      </div>

      {status && <div className="relative z-10 mx-auto mt-6 rounded-full bg-black/60 px-4 py-2 text-sm text-white" data-testid="live-status">{status}</div>}
      {ended && <div className="absolute inset-0 z-20 grid place-items-center"><div className="rounded-2xl bg-[var(--rc-surface)] px-6 py-5 text-center"><div className="text-lg font-semibold mb-3">{t("live_ended")}</div><button onClick={onClose} className="rounded-full bg-[var(--rc-gold)] text-black font-semibold px-5 py-2" data-testid="live-ended-close">{t("s_close") || "Close"}</button></div></div>}

      {/* Floating reactions */}
      <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
        {floats.map((f) => (
          <span key={f.id} className="absolute bottom-28 text-3xl rc-float-up" style={{ left: `${f.left}%` }}>{f.emoji}</span>
        ))}
      </div>

      {/* Pinned product (visible to everyone; viewers tap to open) */}
      {pinned && (
        <div className="relative z-10 mx-3 mb-2">
          <div onClick={isHost ? undefined : openPinned} data-testid="live-pinned" className={`flex items-center gap-3 rounded-xl bg-black/60 border border-[var(--rc-gold)]/50 p-2 ${isHost ? "" : "cursor-pointer"}`}>
            {pinned.image ? <img src={fileUrl(pinned.image)} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="h-12 w-12 rounded-lg bg-white/10 grid place-items-center"><ShoppingBag size={20} className="text-[var(--rc-gold)]" /></div>}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-white">{pinned.title}</div>
              <div className="text-sm text-[var(--rc-gold)] font-semibold">{pinned.promo_price ? <>${pinned.promo_price} <span className="line-through text-white/50 text-xs">${pinned.price}</span></> : `$${pinned.price}`}</div>
            </div>
            {isHost ? <button onClick={unpin} data-testid="live-unpin" className="h-8 w-8 grid place-items-center rounded-full bg-white/15 text-white"><X size={16} /></button>
              : <span className="rounded-full bg-[var(--rc-gold)] text-black text-xs font-semibold px-3 py-1.5">{t("live_buy")}</span>}
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="relative z-10 mt-auto px-3 pb-2 space-y-1.5 max-h-[38vh] overflow-y-auto no-scrollbar" data-testid="live-comments">
        {comments.map((c) => (
          <div key={c.id} className="text-sm text-white drop-shadow" data-testid="live-comment">
            <span className="font-semibold text-[var(--rc-gold)]">{c.name}</span> <span className="[overflow-wrap:anywhere]">{c.text}</span>
          </div>
        ))}
      </div>

      {/* Bottom controls */}
      <div className="relative z-10 flex items-center gap-2 p-3 pb-5">
        <div className="flex flex-1 items-center gap-2 rounded-full bg-black/40 border border-white/15 px-3">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
            placeholder={t("live_say_something")} data-testid="live-comment-input"
            className="flex-1 min-w-0 bg-transparent py-2.5 text-sm text-white placeholder:text-white/60 focus:outline-none" />
          <button onClick={sendComment} data-testid="live-comment-send" className="text-[var(--rc-gold)]"><Send size={18} /></button>
        </div>
        {isHost ? (
          <>
            {(live || session)?.module === "market" && <button onClick={openPinPicker} data-testid="live-pin-btn" className="h-11 w-11 grid place-items-center rounded-full bg-white/15 text-[var(--rc-gold)]"><Pin size={19} /></button>}
            <button onClick={() => setShowFilters((s) => !s)} data-testid="live-filter-btn" className={`h-11 w-11 grid place-items-center rounded-full ${filter !== "none" ? "bg-[var(--rc-gold)] text-black" : "bg-white/15 text-white"}`}><Sparkles size={19} /></button>
            <button onClick={toggleMic} data-testid="live-mic" className="h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white">{micOn ? <Mic size={19} /> : <MicOff size={19} />}</button>
            <button onClick={() => setDataSaver((d) => !d)} data-testid="live-datasaver" className={`h-11 w-11 grid place-items-center rounded-full ${dataSaver ? "bg-[var(--rc-gold)] text-black" : "bg-white/15 text-white"}`} title="Data saver"><Gauge size={19} /></button>
            <button onClick={switchCamera} data-testid="live-switch" className="h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white"><SwitchCamera size={19} /></button>
          </>
        ) : (
          <>
            <button onClick={() => setViewerMuted((m) => !m)} data-testid="live-viewer-mute" className="h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white">{viewerMuted ? <VolumeX size={19} /> : <Volume2 size={19} />}</button>
            <button onClick={() => sendReaction("❤️")} data-testid="live-heart" className="h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white"><Heart size={20} fill="#ef4444" color="#ef4444" /></button>
          </>
        )}
      </div>

      {/* Camera filter picker (host) */}
      {isHost && showFilters && (
        <div className="relative z-10 flex items-center gap-2 overflow-x-auto no-scrollbar px-3 pb-2" data-testid="live-filters">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => applyFilter(f.key)} data-testid={`live-filter-${f.key}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium border ${filter === f.key ? "bg-[var(--rc-gold)] text-black border-[var(--rc-gold)]" : "bg-black/40 text-white border-white/20"}`}>
              {t(`live_filter_${f.key}`)}
            </button>
          ))}
        </div>
      )}

      {/* Reaction strip */}
      <div className="relative z-10 flex items-center justify-center gap-1.5 pb-4">
        {LIVE_EMOJIS.map((e) => (
          <button key={e} onClick={() => sendReaction(e)} data-testid={`live-react-${e}`} className="text-2xl transition-transform hover:scale-125 active:scale-95">{e}</button>
        ))}
      </div>

      {showPin && (
        <div className="absolute inset-0 z-30 flex items-end bg-black/60" onClick={() => setShowPin(false)} data-testid="live-pin-picker">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-h-[60vh] overflow-y-auto rounded-t-2xl bg-[var(--rc-surface)] p-4">
            <div className="font-semibold mb-3">{t("live_pin_product")}</div>
            {myProducts.length === 0 ? <div className="text-sm text-[var(--rc-muted)] py-6 text-center">—</div>
              : <div className="space-y-2">{myProducts.map((p) => (
                  <button key={p.id} onClick={() => pinProduct(p.id)} data-testid={`live-pin-product-${p.id}`} className="w-full flex items-center gap-3 rounded-xl bg-[var(--rc-bg)] p-2 text-left">
                    {(p.images || [])[0] ? <img src={fileUrl(p.images[0])} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="h-12 w-12 rounded-lg bg-white/5 grid place-items-center"><ShoppingBag size={18} /></div>}
                    <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{p.title}</div><div className="text-xs text-[var(--rc-gold)]">${p.promo_price || p.price}</div></div>
                  </button>
                ))}</div>}
          </div>
        </div>
      )}

      {posting && <div className="absolute inset-0 z-40 grid place-items-center bg-black/70" data-testid="live-posting"><div className="flex items-center gap-2 text-white"><Loader2 className="animate-spin" size={20} /> {t("live_posting_replay")}</div></div>}
    </div>
  );
}
